import React, { useState, useEffect, useCallback } from "react";
import { useTheme } from "../DataContext";
import axios from "axios";

const API_REG     = "https://pmo-platform.onrender.com/api/registry";
const API_TENANTS = "https://pmo-platform.onrender.com/api/tenants";

const TIER = {
  initiative: { label: "Initiative",  icon: "◈", color: "#C9A84C", tier: 1 },
  portfolio:  { label: "Portfolio",   icon: "⬡", color: "#00D4FF", tier: 2 },
  program:    { label: "Programme",   icon: "◉", color: "#A78BFA", tier: 3 },
  project:    { label: "Project",     icon: "▦", color: "#00E676", tier: 4 },
};

const STATUS_COLORS = {
  active: "#00E676", registered: "#C9A84C", execution: "#00D4FF",
  closed: "#6B8DB0", archived: "#444", on_hold: "#FFB800",
  completed: "#C9A84C", cancelled: "#FF3D57",
};

const RAG_COLORS = { red: "#FF3D57", amber: "#FFB800", green: "#00E676" };
const PLAN_COLORS = { enterprise: "#C9A84C", standard: "#00D4FF", demo: "#A78BFA" };

const fmt$ = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

// ── Small reusable bits ───────────────────────────────────────────────────────

function Badge({ label, color = "#8BB8D8" }) {
  return (
    <span style={{
      fontSize: "9px", padding: "2px 8px", borderRadius: "10px",
      background: `${color}22`, color, border: `1px solid ${color}44`,
      fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase",
    }}>{label}</span>
  );
}

function RAGDot({ rag }) {
  const c = RAG_COLORS[rag] || "#8BB8D8";
  return <span style={{
    display: "inline-block", width: "8px", height: "8px", borderRadius: "50%",
    background: c, boxShadow: `0 0 5px ${c}88`, flexShrink: 0,
  }} title={rag?.toUpperCase()} />;
}

function Card({ children, theme, style = {} }) {
  return (
    <div style={{
      background: theme.card, border: `1px solid ${theme.cardBorder}`,
      borderRadius: "8px", padding: "16px", ...style,
    }}>{children}</div>
  );
}

function SectionTitle({ theme, children }) {
  return (
    <div style={{
      fontFamily: "'Orbitron', monospace", fontSize: "11px", fontWeight: 700,
      color: theme.accent, letterSpacing: "2px", textTransform: "uppercase",
      marginBottom: "12px", paddingBottom: "8px",
      borderBottom: `1px solid ${theme.cardBorder}`,
    }}>{children}</div>
  );
}

function Btn({ children, onClick, color, small, style = {} }) {
  const bg = color || "#1a2744";
  return (
    <button onClick={onClick} style={{
      padding: small ? "4px 10px" : "8px 14px",
      fontSize: small ? "10px" : "12px", fontWeight: 700,
      background: bg, color: "#fff", border: "none", borderRadius: "6px",
      cursor: "pointer", letterSpacing: "0.5px", ...style,
    }}>{children}</button>
  );
}

function Input({ label, value, onChange, type = "text", theme, required, placeholder }) {
  return (
    <label style={{ display: "grid", gap: "4px" }}>
      <span style={{ fontSize: "10px", color: theme.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}{required && <span style={{ color: "#FF3D57" }}> *</span>}
      </span>
      <input
        type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          padding: "8px 10px", background: theme.inputBg, color: theme.textPrimary,
          border: `1px solid ${theme.cardBorder}`, borderRadius: "6px",
          fontSize: "12px", width: "100%", boxSizing: "border-box",
        }}
      />
    </label>
  );
}

function Select({ label, value, onChange, options, theme }) {
  return (
    <label style={{ display: "grid", gap: "4px" }}>
      <span style={{ fontSize: "10px", color: theme.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)} style={{
        padding: "8px 10px", background: theme.inputBg, color: theme.textPrimary,
        border: `1px solid ${theme.cardBorder}`, borderRadius: "6px", fontSize: "12px",
      }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

// ── Tenant Selector ───────────────────────────────────────────────────────────

function TenantSelector({ tenants, activeTenant, setActiveTenant, onSeedDemo, theme }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ id: "", name: "", plan: "standard", contact: "", org_size: "medium" });
  const [msg, setMsg] = useState("");

  const handleCreate = async () => {
    if (!form.id || !form.name) { setMsg("ID and Name are required"); return; }
    try {
      await axios.post(API_TENANTS, form);
      setMsg("Tenant created"); setCreating(false);
      setForm({ id: "", name: "", plan: "standard", contact: "", org_size: "medium" });
      window.location.reload();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Error creating tenant");
    }
  };

  return (
    <Card theme={theme} style={{ marginBottom: "16px" }}>
      <SectionTitle theme={theme}>Company / Tenant</SectionTitle>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
        {tenants.map(t => (
          <button key={t.id} onClick={() => setActiveTenant(t)}
            style={{
              padding: "8px 14px", borderRadius: "8px", cursor: "pointer",
              fontSize: "11px", fontWeight: 700, letterSpacing: "0.5px",
              background: activeTenant?.id === t.id ? `${PLAN_COLORS[t.plan] || "#C9A84C"}33` : theme.inputBg,
              border: `1px solid ${activeTenant?.id === t.id ? (PLAN_COLORS[t.plan] || "#C9A84C") : theme.cardBorder}`,
              color: activeTenant?.id === t.id ? (PLAN_COLORS[t.plan] || "#C9A84C") : theme.textSecondary,
            }}>
            {t.name}
            <span style={{ marginLeft: "6px", fontSize: "9px", opacity: 0.7 }}>{t.plan?.toUpperCase()}</span>
          </button>
        ))}
        <Btn small color="#1a2744" onClick={() => setCreating(!creating)}>+ New Company</Btn>
        <Btn small color="#2a1744" onClick={onSeedDemo}>Seed Demo Companies</Btn>
      </div>

      {activeTenant && (
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "11px", color: theme.textMuted }}>
            ID: <span style={{ color: theme.accent, fontFamily: "monospace" }}>{activeTenant.id}</span>
          </span>
          <span style={{ fontSize: "11px", color: theme.textMuted }}>
            Users: <span style={{ color: theme.textPrimary }}>{activeTenant.user_count ?? "—"}</span>
          </span>
          <span style={{ fontSize: "11px", color: theme.textMuted }}>
            Initiatives: <span style={{ color: theme.textPrimary }}>{activeTenant.initiative_count ?? "—"}</span>
          </span>
          <Badge label={activeTenant.plan} color={PLAN_COLORS[activeTenant.plan] || "#8BB8D8"} />
          {activeTenant.active ? <Badge label="Active" color="#00E676" /> : <Badge label="Inactive" color="#FF3D57" />}
        </div>
      )}

      {creating && (
        <div style={{ marginTop: "14px", padding: "14px", background: theme.inputBg, borderRadius: "8px", border: `1px solid ${theme.cardBorder}` }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
            <Input label="Tenant ID" value={form.id} onChange={v => setForm(f => ({ ...f, id: v }))} theme={theme} required placeholder="T004" />
            <Input label="Company Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} theme={theme} required placeholder="Acme Corp" />
            <Select label="Plan" value={form.plan} onChange={v => setForm(f => ({ ...f, plan: v }))} theme={theme}
              options={[{ value: "demo", label: "Demo" }, { value: "standard", label: "Standard" }, { value: "enterprise", label: "Enterprise" }]} />
            <Select label="Org Size" value={form.org_size} onChange={v => setForm(f => ({ ...f, org_size: v }))} theme={theme}
              options={[{ value: "small", label: "Small" }, { value: "medium", label: "Medium" }, { value: "large", label: "Large" }, { value: "enterprise", label: "Enterprise" }]} />
            <Input label="Contact Email" value={form.contact} onChange={v => setForm(f => ({ ...f, contact: v }))} theme={theme} placeholder="pmo@company.com" />
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Btn small color="#C9A84C" onClick={handleCreate}>Create Tenant</Btn>
            <Btn small onClick={() => setCreating(false)}>Cancel</Btn>
            {msg && <span style={{ fontSize: "11px", color: "#FFB800" }}>{msg}</span>}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Entity Form ───────────────────────────────────────────────────────────────

function EntityForm({ type, tenantId, parentOptions, onCreated, onCancel, theme }) {
  const initials = {
    initiative: { id: "", name: "", owner: "", sponsor: "", strategic_theme: "", budget_envelope: "", start_date: "", target_date: "", status: "active", priority: "5" },
    portfolio:  { id: "", initiative_id: "", name: "", owner: "", budget_total: "", start_date: "", end_date: "", status: "active" },
    program:    { id: "", portfolio_id: "", name: "", program_manager: "", sponsor: "", budget: "", start_date: "", end_date: "", status: "active", rag: "green" },
    project:    { id: "", program_id: "", name: "", assigned_pm: "", sponsor: "", budget: "", start_date: "", end_date: "", baseline_end_date: "", framework: "waterfall", status: "registered" },
  };
  const [form, setForm] = useState(initials[type] || {});
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState("");

  const f = v => setForm(p => ({ ...p, ...v }));
  const cfg = TIER[type];

  const handleSubmit = async () => {
    if (!form.id || !form.name) { setMsg("ID and Name are required"); return; }
    try {
      const endpointMap = { initiative: "initiatives", portfolio: "portfolios", program: "programs", project: "projects" };
      await axios.post(`${API_REG}/${endpointMap[type]}`, { ...form, tenant_id: tenantId, changed_by: "admin", reason });
      setMsg("Created successfully");
      setTimeout(() => { onCreated(); }, 700);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Error creating entity");
    }
  };

  return (
    <div style={{ padding: "14px", background: theme.inputBg, borderRadius: "8px", border: `1px solid ${cfg.color}44` }}>
      <div style={{ fontSize: "11px", color: cfg.color, fontWeight: 700, marginBottom: "12px", letterSpacing: "1px" }}>
        {cfg.icon} NEW {cfg.label.toUpperCase()}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
        <Input label={`${cfg.label} Code / ID`} value={form.id} onChange={v => f({ id: v })} theme={theme} required
          placeholder={type === "project" ? "PRJ-2026-001" : `${type[0].toUpperCase()}001`} />
        <Input label="Name" value={form.name} onChange={v => f({ name: v })} theme={theme} required />

        {type === "initiative" && <>
          <Input label="Owner" value={form.owner} onChange={v => f({ owner: v })} theme={theme} />
          <Input label="Sponsor" value={form.sponsor} onChange={v => f({ sponsor: v })} theme={theme} />
          <Input label="Strategic Theme" value={form.strategic_theme} onChange={v => f({ strategic_theme: v })} theme={theme} />
          <Input label="Budget Envelope ($)" value={form.budget_envelope} onChange={v => f({ budget_envelope: v })} type="number" theme={theme} />
          <Input label="Start Date" value={form.start_date} onChange={v => f({ start_date: v })} type="date" theme={theme} />
          <Input label="Target Date" value={form.target_date} onChange={v => f({ target_date: v })} type="date" theme={theme} />
        </>}

        {type === "portfolio" && <>
          <Select label="Parent Initiative" value={form.initiative_id} onChange={v => f({ initiative_id: v })} theme={theme}
            options={[{ value: "", label: "— None (standalone) —" }, ...parentOptions.map(p => ({ value: p.id, label: p.name }))]} />
          <Input label="Owner" value={form.owner} onChange={v => f({ owner: v })} theme={theme} />
          <Input label="Budget ($)" value={form.budget_total} onChange={v => f({ budget_total: v })} type="number" theme={theme} />
          <Input label="Start Date" value={form.start_date} onChange={v => f({ start_date: v })} type="date" theme={theme} />
          <Input label="End Date" value={form.end_date} onChange={v => f({ end_date: v })} type="date" theme={theme} />
        </>}

        {type === "program" && <>
          <Select label="Parent Portfolio" value={form.portfolio_id} onChange={v => f({ portfolio_id: v })} theme={theme}
            options={[{ value: "", label: "— None —" }, ...parentOptions.map(p => ({ value: p.id, label: p.name }))]} />
          <Input label="Programme Manager" value={form.program_manager} onChange={v => f({ program_manager: v })} theme={theme} />
          <Input label="Sponsor" value={form.sponsor} onChange={v => f({ sponsor: v })} theme={theme} />
          <Input label="Budget ($)" value={form.budget} onChange={v => f({ budget: v })} type="number" theme={theme} />
          <Input label="Start Date" value={form.start_date} onChange={v => f({ start_date: v })} type="date" theme={theme} />
          <Input label="End Date" value={form.end_date} onChange={v => f({ end_date: v })} type="date" theme={theme} />
        </>}

        {type === "project" && <>
          <Select label="Parent Programme" value={form.program_id} onChange={v => f({ program_id: v })} theme={theme}
            options={[{ value: "", label: "— Standalone (no programme) —" }, ...parentOptions.map(p => ({ value: p.id, label: p.name }))]} />
          <Input label="Assigned PM (username)" value={form.assigned_pm} onChange={v => f({ assigned_pm: v })} theme={theme} placeholder="e.g. alice.pm" />
          <Input label="Sponsor" value={form.sponsor} onChange={v => f({ sponsor: v })} theme={theme} />
          <Input label="Authorised Budget ($)" value={form.budget} onChange={v => f({ budget: v })} type="number" theme={theme} />
          <Input label="Start Date" value={form.start_date} onChange={v => f({ start_date: v })} type="date" theme={theme} />
          <Input label="Baseline End Date" value={form.baseline_end_date} onChange={v => f({ baseline_end_date: v })} type="date" theme={theme} />
          <Select label="Framework" value={form.framework} onChange={v => f({ framework: v })} theme={theme}
            options={[{ value: "waterfall", label: "Waterfall / Predictive" }, { value: "agile", label: "Agile / Scrum" }, { value: "hybrid", label: "Hybrid" }]} />
          <Input label="Authorisation Reason" value={reason} onChange={setReason} theme={theme} placeholder="Board approval ref #..." />
        </>}
      </div>
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <Btn small color={cfg.color} onClick={handleSubmit}>Create {cfg.label}</Btn>
        <Btn small onClick={onCancel}>Cancel</Btn>
        {msg && <span style={{ fontSize: "11px", color: msg.includes("success") ? "#00E676" : "#FFB800" }}>{msg}</span>}
      </div>
    </div>
  );
}

// ── XML Upload Panel ──────────────────────────────────────────────────────────

function XmlUploadPanel({ project, tenantId, onUploaded, theme }) {
  const [file, setFile] = useState(null);
  const [uploaderName, setUploaderName] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const handleUpload = async () => {
    if (!file) { setMsg("Select an XML file first"); return; }
    if (!uploaderName) { setMsg("Enter your username"); return; }
    const form = new FormData();
    form.append("file", file);
    form.append("uploaded_by", uploaderName);
    form.append("tenant_id", tenantId);
    setLoading(true);
    try {
      const res = await axios.post(`${API_REG}/projects/${project.id}/upload-xml`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsg(`✓ v${res.data.version} uploaded — ${res.data.summary.task_count} tasks`);
      setFile(null); setLoading(false);
      setTimeout(onUploaded, 1200);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Upload failed");
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "12px", background: `${TIER.project.color}11`, border: `1px dashed ${TIER.project.color}55`, borderRadius: "8px" }}>
      <div style={{ fontSize: "10px", color: TIER.project.color, fontWeight: 700, marginBottom: "8px", letterSpacing: "1px" }}>
        MS PROJECT XML UPLOAD — {project.id}
      </div>
      {project.assigned_pm && (
        <div style={{ fontSize: "10px", color: "#FFB800", marginBottom: "8px" }}>
          ⚠ Locked to PM: <strong>{project.assigned_pm}</strong>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
        <Input label="Your Username" value={uploaderName} onChange={setUploaderName} theme={theme} placeholder={project.assigned_pm || "username"} />
        <label style={{ display: "grid", gap: "4px" }}>
          <span style={{ fontSize: "10px", color: theme.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>XML File</span>
          <input type="file" accept=".xml,application/xml,text/xml"
            onChange={e => setFile(e.target.files[0])}
            style={{ fontSize: "11px", color: theme.textSecondary, padding: "6px" }} />
        </label>
      </div>
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <Btn small color={TIER.project.color} onClick={handleUpload}>{loading ? "Uploading…" : "Upload XML"}</Btn>
        {msg && <span style={{ fontSize: "10px", color: msg.startsWith("✓") ? "#00E676" : "#FF3D57" }}>{msg}</span>}
      </div>
      {project.xml_uploads?.length > 0 && (
        <div style={{ marginTop: "8px" }}>
          <div style={{ fontSize: "9px", color: theme.textMuted, marginBottom: "4px" }}>VERSION HISTORY</div>
          {project.xml_uploads.slice(0, 5).map(u => (
            <div key={u.id} style={{ fontSize: "10px", color: theme.textSecondary, padding: "3px 0", borderBottom: `1px solid ${theme.cardBorder}` }}>
              v{u.version_num} · {u.filename} · {u.task_count} tasks · {u.uploaded_by} · {u.uploaded_at?.slice(0, 10)}
              {u.is_current && <span style={{ marginLeft: "6px" }}><Badge label="Current" color="#00E676" /></span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Changelog Panel ───────────────────────────────────────────────────────────

function ChangelogPanel({ tenantId, entityId, theme }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    const url = entityId
      ? `${API_TENANTS}/${tenantId}/changelog?entity_id=${entityId}`
      : `${API_TENANTS}/${tenantId}/changelog`;
    axios.get(url).then(r => { setLogs(r.data.changelog || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [tenantId, entityId]);

  if (loading) return <div style={{ fontSize: "11px", color: "#6B8DB0", padding: "8px" }}>Loading audit trail…</div>;
  if (!logs.length) return <div style={{ fontSize: "11px", color: "#6B8DB0", padding: "8px" }}>No controlled-field changes recorded yet.</div>;

  return (
    <div style={{ maxHeight: "360px", overflowY: "auto" }}>
      {logs.map(log => (
        <div key={log.id} style={{ padding: "8px 10px", borderBottom: `1px solid ${theme.cardBorder}`, fontSize: "11px" }}>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "2px" }}>
            <span style={{ color: theme.textMuted, fontFamily: "monospace", fontSize: "10px" }}>{log.changed_at?.slice(0, 16)}</span>
            <Badge label={log.entity_type} color={TIER[log.entity_type]?.color || "#8BB8D8"} />
            <span style={{ color: theme.textPrimary, fontWeight: 700 }}>{log.entity_name || log.entity_id}</span>
          </div>
          <div style={{ color: theme.textSecondary }}>
            <span style={{ color: "#FFB800" }}>{log.field_name}</span>
            {" "} changed from <span style={{ color: "#FF3D57" }}>{log.old_value || "—"}</span>
            {" "} → <span style={{ color: "#00E676" }}>{log.new_value}</span>
            {" "} by <strong>{log.changed_by}</strong>
          </div>
          {log.reason && <div style={{ color: theme.textMuted, marginTop: "2px" }}>Reason: {log.reason}</div>}
        </div>
      ))}
    </div>
  );
}

// ── Tree Node ─────────────────────────────────────────────────────────────────

function TreeNode({ type, data, depth = 0, theme, tenantId, onRefresh, selectedId, setSelectedId }) {
  const [open, setOpen] = useState(depth < 2);
  const [showUpload, setShowUpload] = useState(false);
  const cfg = TIER[type];
  const isSelected = selectedId === data.id;

  const children =
    type === "initiative" ? (data.portfolios || []).map(pf => ({ type: "portfolio", data: pf })) :
    type === "portfolio"  ? (data.programs   || []).map(pr => ({ type: "program",   data: pr })) :
    type === "program"    ? (data.projects   || []).map(p  => ({ type: "project",   data: p  })) :
    [];

  return (
    <div style={{ marginLeft: depth > 0 ? "20px" : "0", marginBottom: "3px" }}>
      <div
        onClick={() => { setOpen(o => !o); setSelectedId(data.id); }}
        style={{
          display: "flex", alignItems: "center", gap: "8px", padding: "6px 10px",
          borderRadius: "7px", cursor: "pointer",
          background: isSelected ? `${cfg.color}18` : "transparent",
          border: `1px solid ${isSelected ? cfg.color + "55" : "transparent"}`,
          transition: "all 0.15s",
        }}
      >
        <span style={{ color: cfg.color, fontSize: "13px", flexShrink: 0 }}>{cfg.icon}</span>
        <span style={{ flex: 1, fontSize: "12px", fontWeight: 600, color: theme.textPrimary }}>{data.name}</span>
        <span style={{ fontSize: "9px", color: theme.textMuted, fontFamily: "monospace" }}>{data.id}</span>
        {data.rag && <RAGDot rag={data.rag} />}
        {data.status && <Badge label={data.status} color={STATUS_COLORS[data.status] || "#8BB8D8"} />}
        {type === "project" && data.xml_uploaded && <Badge label={`v${data.xml_version}`} color={TIER.project.color} />}
        {type === "project" && !data.xml_uploaded && <Badge label="No XML" color="#FFB800" />}
        {(Number(data.budget) || Number(data.budget_total) || Number(data.budget_envelope)) > 0 && (
          <span style={{ fontSize: "10px", color: "#C9A84C", fontFamily: "monospace" }}>
            {fmt$(data.budget || data.budget_total || data.budget_envelope)}
          </span>
        )}
        {children.length > 0 && (
          <span style={{ fontSize: "10px", color: theme.textMuted }}>{open ? "▾" : "▸"} {children.length}</span>
        )}
        {type === "project" && (
          <button onClick={e => { e.stopPropagation(); setShowUpload(u => !u); }}
            style={{
              padding: "2px 8px", fontSize: "9px",
              background: showUpload ? `${TIER.project.color}33` : "transparent",
              border: `1px solid ${TIER.project.color}55`, borderRadius: "5px",
              color: TIER.project.color, cursor: "pointer", fontWeight: 700,
            }}>XML ↑</button>
        )}
      </div>

      {type === "project" && showUpload && (
        <div style={{ marginLeft: "28px", marginTop: "6px", marginBottom: "6px" }}>
          <XmlUploadPanel project={data} tenantId={tenantId} onUploaded={onRefresh} theme={theme} />
        </div>
      )}

      {open && children.map((child, i) => (
        <TreeNode key={i} {...child} depth={depth + 1} theme={theme} tenantId={tenantId}
          onRefresh={onRefresh} selectedId={selectedId} setSelectedId={setSelectedId} />
      ))}
    </div>
  );
}

// ── Tenant Users Panel ────────────────────────────────────────────────────────

function TenantUsersPanel({ tenantId, theme }) {
  const [users, setUsers]     = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm]       = useState({ id: "", username: "", full_name: "", email: "", role: "pm" });
  const [msg, setMsg]         = useState("");

  useEffect(() => {
    axios.get(`${API_TENANTS}/${tenantId}/users`)
      .then(r => setUsers(r.data.users || []))
      .catch(() => {});
  }, [tenantId]);

  const handleCreate = async () => {
    if (!form.id || !form.username) { setMsg("ID and Username required"); return; }
    try {
      await axios.post(`${API_TENANTS}/${tenantId}/users`, { ...form, tenant_id: tenantId });
      const r = await axios.get(`${API_TENANTS}/${tenantId}/users`);
      setUsers(r.data.users || []);
      setCreating(false); setForm({ id: "", username: "", full_name: "", email: "", role: "pm" });
      setMsg("User created");
    } catch (e) { setMsg(e.response?.data?.detail || "Error"); }
  };

  const ROLE_COLOR = { admin: "#FF6B35", pmodirector: "#00D4FF", pm: "#00E676", viewer: "#8BB8D8" };

  return (
    <div>
      <Card theme={theme} style={{ marginBottom: "12px" }}>
        <SectionTitle theme={theme}>Team Members</SectionTitle>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>{["ID", "Username", "Full Name", "Email", "Role", "Active"].map(h => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontSize: "10px", color: theme.textMuted, textTransform: "uppercase", borderBottom: `1px solid ${theme.cardBorder}`, background: theme.inputBg }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={{ padding: "8px 10px", fontSize: "11px", color: theme.textMuted, fontFamily: "monospace", borderBottom: `1px solid ${theme.cardBorder}` }}>{u.id}</td>
                  <td style={{ padding: "8px 10px", fontSize: "12px", fontWeight: 700, color: theme.textPrimary, borderBottom: `1px solid ${theme.cardBorder}` }}>{u.username}</td>
                  <td style={{ padding: "8px 10px", fontSize: "12px", color: theme.textSecondary, borderBottom: `1px solid ${theme.cardBorder}` }}>{u.full_name || "—"}</td>
                  <td style={{ padding: "8px 10px", fontSize: "11px", color: theme.textSecondary, borderBottom: `1px solid ${theme.cardBorder}` }}>{u.email || "—"}</td>
                  <td style={{ padding: "8px 10px", borderBottom: `1px solid ${theme.cardBorder}` }}><Badge label={u.role} color={ROLE_COLOR[u.role] || "#8BB8D8"} /></td>
                  <td style={{ padding: "8px 10px", borderBottom: `1px solid ${theme.cardBorder}` }}><Badge label={u.active ? "Active" : "Inactive"} color={u.active ? "#00E676" : "#FF3D57"} /></td>
                </tr>
              ))}
              {!users.length && <tr><td colSpan={6} style={{ padding: "16px", textAlign: "center", color: theme.textMuted, fontSize: "12px" }}>No users yet</td></tr>}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: "12px" }}>
          <Btn small color="#C9A84C" onClick={() => setCreating(c => !c)}>+ Add User</Btn>
          {msg && <span style={{ marginLeft: "10px", fontSize: "11px", color: "#00E676" }}>{msg}</span>}
        </div>
      </Card>

      {creating && (
        <Card theme={theme}>
          <SectionTitle theme={theme}>New User</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
            <Input label="User ID" value={form.id} onChange={v => setForm(f => ({ ...f, id: v }))} theme={theme} required placeholder="TU007" />
            <Input label="Username" value={form.username} onChange={v => setForm(f => ({ ...f, username: v }))} theme={theme} required placeholder="john.pm" />
            <Input label="Full Name" value={form.full_name} onChange={v => setForm(f => ({ ...f, full_name: v }))} theme={theme} />
            <Input label="Email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} theme={theme} />
            <Select label="Role" value={form.role} onChange={v => setForm(f => ({ ...f, role: v }))} theme={theme}
              options={[{ value: "pm", label: "Project Manager" }, { value: "pmodirector", label: "PMO Director" }, { value: "admin", label: "Admin" }, { value: "viewer", label: "Viewer" }]} />
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Btn small color="#C9A84C" onClick={handleCreate}>Create User</Btn>
            <Btn small onClick={() => setCreating(false)}>Cancel</Btn>
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PortfolioRegistry() {
  const { theme } = useTheme();
  const [tenants, setTenants]           = useState([]);
  const [activeTenant, setActiveTenant] = useState(null);
  const [hierarchy, setHierarchy]       = useState([]);
  const [standalone, setStandalone]     = useState([]);
  const [summary, setSummary]           = useState(null);
  const [selectedId, setSelectedId]     = useState(null);
  const [activeTab, setActiveTab]       = useState("tree");
  const [creating, setCreating]         = useState(null);
  const [parentOpts, setParentOpts]     = useState([]);
  const [loading, setLoading]           = useState(false);
  const [msg, setMsg]                   = useState("");

  const loadTenants = useCallback(async () => {
    try {
      const r = await axios.get(API_TENANTS);
      const list = r.data.tenants || [];
      setTenants(list);
      if (!activeTenant && list.length) setActiveTenant(list[0]);
    } catch { setMsg("Backend offline — start the FastAPI server on port 8000"); }
  }, [activeTenant]);

  const loadHierarchy = useCallback(async () => {
    if (!activeTenant) return;
    setLoading(true);
    try {
      const [hier, sum] = await Promise.all([
        axios.get(`${API_REG}/hierarchy?tenant_id=${activeTenant.id}`),
        axios.get(`${API_REG}/summary?tenant_id=${activeTenant.id}`),
      ]);
      setHierarchy(hier.data.hierarchy || []);
      setStandalone(hier.data.standalone_projects || []);
      setSummary(sum.data);
    } catch { setMsg("Failed to load hierarchy"); }
    setLoading(false);
  }, [activeTenant]);

  useEffect(() => { loadTenants(); }, []);
  useEffect(() => { if (activeTenant) loadHierarchy(); }, [activeTenant]);

  const seedDemo = async () => {
    try {
      await axios.post(`${API_TENANTS}/seed-demo`);
      await loadTenants();
      setMsg("3 demo companies seeded (Acme, Global Infra, Digital Ventures)");
    } catch (e) { setMsg(e.response?.data?.detail || "Seed failed"); }
  };

  const seedHierarchy = async () => {
    if (!activeTenant) return;
    try {
      await axios.post(`${API_REG}/seed?tenant_id=${activeTenant.id}`);
      await loadHierarchy();
      setMsg("Demo PPPI hierarchy seeded for " + activeTenant.name);
    } catch (e) { setMsg(e.response?.data?.detail || "Seed failed"); }
  };

  const startCreate = async (type) => {
    setCreating(type);
    const parentEndpoint = { portfolio: "initiatives", program: "portfolios", project: "programs" }[type];
    if (parentEndpoint && activeTenant) {
      try {
        const r = await axios.get(`${API_REG}/${parentEndpoint}?tenant_id=${activeTenant.id}`);
        setParentOpts(r.data[parentEndpoint] || []);
      } catch { setParentOpts([]); }
    }
  };

  const tabs = [
    { id: "tree",      label: "PPPI Tree" },
    { id: "changelog", label: "Audit Trail" },
    { id: "users",     label: "Users" },
  ];

  return (
    <div style={{ padding: "24px", fontFamily: "'Exo 2', Arial, sans-serif", color: theme.textPrimary }}>

      <div style={{ marginBottom: "20px" }}>
        <div style={{ fontFamily: "'Orbitron', monospace", fontSize: "18px", fontWeight: 900, color: theme.textPrimary, marginBottom: "4px" }}>
          PPPI Registry
        </div>
        <div style={{ fontSize: "12px", color: theme.textMuted, fontFamily: "monospace" }}>
          Initiatives → Portfolios → Programmes → Projects · Multi-tenant · Audit-logged · XML-locked
        </div>
      </div>

      {msg && (
        <div style={{ padding: "8px 14px", background: "#FFB80022", border: "1px solid #FFB800", borderRadius: "6px", fontSize: "11px", color: "#FFB800", marginBottom: "12px", display: "flex", justifyContent: "space-between" }}>
          <span>{msg}</span>
          <button onClick={() => setMsg("")} style={{ background: "none", border: "none", color: "#FFB800", cursor: "pointer" }}>✕</button>
        </div>
      )}

      <TenantSelector tenants={tenants} activeTenant={activeTenant}
        setActiveTenant={t => { setActiveTenant(t); setSelectedId(null); setSummary(null); }}
        onSeedDemo={seedDemo} theme={theme} />

      {activeTenant && (
        <>
          {summary && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "10px", marginBottom: "16px" }}>
              {[
                ["Initiatives", summary.initiatives, "#C9A84C"],
                ["Portfolios",  summary.portfolios,  "#00D4FF"],
                ["Programmes",  summary.programs,    "#A78BFA"],
                ["Projects",    summary.projects,    "#00E676"],
                ["XML Uploads", summary.xml_uploads, "#FF9800"],
              ].map(([label, count, color]) => (
                <Card key={label} theme={theme} style={{ textAlign: "center", padding: "12px" }}>
                  <div style={{ fontSize: "9px", color: theme.textMuted, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "4px" }}>{label}</div>
                  <div style={{ fontSize: "26px", fontWeight: 900, color, fontFamily: "'Share Tech Mono', monospace" }}>{count ?? 0}</div>
                </Card>
              ))}
            </div>
          )}

          <div style={{ display: "flex", gap: "8px", marginBottom: "16px", alignItems: "center" }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                padding: "7px 16px", fontSize: "11px", fontWeight: 700, letterSpacing: "1px",
                background: activeTab === t.id ? `${theme.accent}22` : theme.inputBg,
                border: `1px solid ${activeTab === t.id ? theme.accent : theme.cardBorder}`,
                borderRadius: "6px", color: activeTab === t.id ? theme.accent : theme.textSecondary,
                cursor: "pointer",
              }}>{t.label}</button>
            ))}
            <div style={{ flex: 1 }} />
            <Btn small color="#1a4428" onClick={seedHierarchy}>Seed Demo Hierarchy</Btn>
          </div>

          {/* PPPI Tree */}
          {activeTab === "tree" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: "16px" }}>
              <Card theme={theme}>
                <div style={{ display: "flex", gap: "8px", marginBottom: "14px", flexWrap: "wrap" }}>
                  {["initiative", "portfolio", "program", "project"].map(type => (
                    <Btn key={type} small color={TIER[type].color} onClick={() => startCreate(type)}>
                      {TIER[type].icon} + {TIER[type].label}
                    </Btn>
                  ))}
                  <Btn small color="#333" onClick={loadHierarchy}>↻ Refresh</Btn>
                </div>

                {creating && (
                  <div style={{ marginBottom: "14px" }}>
                    <EntityForm type={creating} tenantId={activeTenant.id}
                      parentOptions={parentOpts}
                      onCreated={() => { setCreating(null); loadHierarchy(); }}
                      onCancel={() => setCreating(null)}
                      theme={theme} />
                  </div>
                )}

                {loading ? (
                  <div style={{ padding: "32px", textAlign: "center", color: theme.textMuted }}>Loading hierarchy…</div>
                ) : hierarchy.length === 0 && standalone.length === 0 ? (
                  <div style={{ padding: "32px", textAlign: "center", color: theme.textMuted }}>
                    No entities yet for <strong style={{ color: theme.accent }}>{activeTenant.name}</strong>.<br />
                    <span style={{ fontSize: "11px" }}>Create an Initiative or seed the demo hierarchy.</span>
                  </div>
                ) : (
                  <>
                    {hierarchy.map((ini, i) => (
                      <TreeNode key={i} type="initiative" data={ini} theme={theme}
                        tenantId={activeTenant.id} onRefresh={loadHierarchy}
                        selectedId={selectedId} setSelectedId={setSelectedId} />
                    ))}
                    {standalone.length > 0 && (
                      <div style={{ marginTop: "14px" }}>
                        <div style={{ fontSize: "10px", color: theme.textMuted, letterSpacing: "1px", marginBottom: "6px", paddingTop: "10px", borderTop: `1px solid ${theme.cardBorder}` }}>
                          STANDALONE PROJECTS
                        </div>
                        {standalone.map((p, i) => (
                          <TreeNode key={i} type="project" data={p} theme={theme}
                            tenantId={activeTenant.id} onRefresh={loadHierarchy}
                            selectedId={selectedId} setSelectedId={setSelectedId} />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </Card>

              <div style={{ display: "grid", gap: "12px", alignContent: "start" }}>
                <Card theme={theme}>
                  <SectionTitle theme={theme}>Hierarchy Legend</SectionTitle>
                  {Object.entries(TIER).map(([type, cfg]) => (
                    <div key={type} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "5px 0", borderBottom: `1px solid ${theme.cardBorder}` }}>
                      <span style={{ color: cfg.color, fontSize: "14px", width: "18px" }}>{cfg.icon}</span>
                      <span style={{ fontSize: "11px", color: theme.textSecondary }}>{cfg.label}</span>
                      <span style={{ fontSize: "9px", color: theme.textMuted, marginLeft: "auto" }}>Tier {cfg.tier}</span>
                    </div>
                  ))}
                </Card>

                <Card theme={theme}>
                  <SectionTitle theme={theme}>Project Lifecycle</SectionTitle>
                  {["registered", "active", "execution", "closed", "archived"].map(s => (
                    <div key={s} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 0" }}>
                      <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: STATUS_COLORS[s], display: "inline-block", flexShrink: 0 }} />
                      <span style={{ fontSize: "11px", color: theme.textSecondary, textTransform: "capitalize" }}>{s}</span>
                    </div>
                  ))}
                </Card>

                <Card theme={theme}>
                  <SectionTitle theme={theme}>Upload Rules</SectionTitle>
                  <div style={{ fontSize: "11px", color: theme.textSecondary, lineHeight: 1.65 }}>
                    • Project code is <strong style={{ color: "#C9A84C" }}>immutable</strong><br />
                    • Only <strong style={{ color: "#00E676" }}>assigned PM</strong> can upload XML<br />
                    • Every upload is <strong style={{ color: "#00D4FF" }}>versioned</strong><br />
                    • Budget &amp; PM changes are <strong style={{ color: "#A78BFA" }}>audit-logged</strong>
                  </div>
                </Card>
              </div>
            </div>
          )}

          {activeTab === "changelog" && (
            <Card theme={theme}>
              <SectionTitle theme={theme}>Controlled Field Audit Trail — {activeTenant.name}</SectionTitle>
              <ChangelogPanel tenantId={activeTenant.id} entityId={selectedId} theme={theme} />
            </Card>
          )}

          {activeTab === "users" && (
            <TenantUsersPanel tenantId={activeTenant.id} theme={theme} />
          )}
        </>
      )}
    </div>
  );
}

